// Shared vanilla CSS for the media switchers (ShowMaterialMediaByContentType,
// ShowBodyMediaByContentType). Prefix: med-. Aggregated into libraryStyles.
// Reuses mc-xl / mc-bold / mc-underline for text/equation rendering.
// NOTE: ShowBodyMedia's fullscreen area used `h-[500]` (no unit → no-op);
// preserved as `.med-fs-area-auto` (no height). ShowMaterialMedia used the valid
// `h-[500px]` → `.med-fs-area` (height: 500px).
export const mediaStyles = `
  .med-root { width: 100%; }
  .med-modal { flex: 1 1 0%; display: flex; flex-direction: column; }
  .med-close { margin-left: auto; padding: 0.75rem 1.25rem; }
  .med-fs-area { display: flex; align-items: center; justify-content: center; height: 500px; }
  .med-fs-area-auto { display: flex; align-items: center; justify-content: center; }
  .med-rel { position: relative; }
  .med-wfull { width: 100%; }
  .med-rounded { border-radius: var(--rad-catchup-xlarge); }
  .med-xl-bold { font-size: var(--fs-xl); font-weight: 700; }

  .med-fs-btn {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0.5rem;
    right: 0.5rem;
    height: 1.5rem;
    width: 1.5rem;
    cursor: pointer;
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    border-color: var(--catchup-blue);
    padding: 0.25rem;
  }
  .med-fs-btn-bg {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0.5rem;
    right: 0.5rem;
    height: 1.5rem;
    width: 1.5rem;
    cursor: pointer;
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    border-color: var(--catchup-blue);
    padding: 0.25rem;
    background-color: rgba(255, 255, 255, 0.8);
  }

  /* video size-from-prop (ShowBodyMedia convertToPercentage) */
  .med-w-small { width: var(--w-small-media); }
  .med-w-medium { width: var(--w-medium-media); }
  .med-w-large { width: var(--w-large-media); }

  /* ShowMaterialMedia */
  .med-mwrap { margin-top: 0.5rem; margin-bottom: 0.5rem; width: 100%; height: 100%; }
  .med-mcenter { height: 100%; display: flex; align-items: center; justify-content: center; }
  .med-mbox {
    height: var(--h-catchup-activity-media-box-item);
    width: var(--w-catchup-activity-media-box-item);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .med-mbox-rel {
    height: var(--h-catchup-activity-media-box-item);
    width: var(--w-catchup-activity-media-box-item);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .med-w-mediabox { width: var(--w-catchup-activity-media-box-item); }
  .med-h-mediabox { height: var(--h-catchup-activity-media-box-item); }
  .med-img-contain { width: 100%; height: 100%; object-fit: contain; }
  .med-img-contain-rounded {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: var(--rad-catchup-xlarge);
  }
  .med-video {
    max-height: 100%;
    max-width: 100%;
    border-radius: var(--rad-catchup-xlarge);
    object-fit: contain;
  }
  .med-audio-wrap { width: 100%; padding: 0.5rem 0; display: flex; align-items: center; }
  .med-audio { width: 100%; border-radius: var(--rad-catchup-xlarge); }

  /* ShowBodyMedia */
  .med-text-wrap {
    margin-bottom: 0.25rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }
  .med-pre { white-space: pre-wrap; }
  .med-img-wrap {
    margin-bottom: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }
  .med-av-wrap {
    margin-bottom: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .med-img200 { position: relative; width: 200px; }
  .med-v200 { width: 200px; border-radius: var(--rad-catchup-xlarge); }
  .med-img-max { max-width: 100%; border-radius: var(--rad-catchup-xlarge); }
  .med-img-inner {
    max-height: 50vh;
    max-width: 100%;
    /* height:auto lets the width/height attributes reserve the aspect-ratio box
       up front (no CLS) while still scaling down responsively. */
    height: auto;
    object-fit: contain;
    border-radius: var(--rad-catchup-xlarge);
  }

  /* ---- Passage IMAGE layout (active only under .am-body-list--passage) ---- */
  /* Layout depends ONLY on orientation, never on the image's uploaded size:
     landscape / standalone span the width; portrait floats so prose wraps it. */

  /* Landscape / standalone / pre-load → full-width block; never upscaled past
     natural width (img keeps max-width:100%). */
  .am-body-list--passage .med-img-wrap.med-pimg-full { width: 100%; }

  /* Portrait → float so the prose wraps it, but only when the passage container
     is wide enough to leave a readable text column beside it. Below the
     threshold (narrow split pane, mobile) it falls back to the base centered
     wrapper. */
  @container pac (min-width: 520px) {
    .am-body-list--passage .med-img-wrap.med-pimg-float {
      display: block;
      float: right;
      /* Hug the image (bounded at 44%) so a portrait figure capped by height
         doesn't leave an empty gap between it and the wrapping prose. */
      width: fit-content;
      max-width: 44%;
      margin: 0.15rem 0 0.5rem 1.25rem;
    }
    /* Cap floated height a touch lower so short following prose still clears it. */
    .am-body-list--passage .med-pimg-float .med-img-inner { max-height: 42vh; }
  }

  /* ---- Open-ended IMAGE layout (active only under .am-body-list--oe) ---- */
  /* Figure floats LEFT and the question prose wraps to its right like a news
     column, keeping the question compact above the answer area. Any image
     floats (not only portrait); below the width threshold (narrow pane, mobile)
     it falls back to the base centered full-width block. */
  .am-body-list--oe .med-img-wrap.med-pimg-full { width: 100%; }
  @container pac (min-width: 520px) {
    .am-body-list--oe .med-img-wrap.med-pimg-float {
      display: block;
      float: left;
      width: fit-content;
      max-width: 44%;
      margin: 0.15rem 1.25rem 0.5rem 0;
    }
    .am-body-list--oe .med-pimg-float .med-img-inner { max-height: 42vh; }
  }
  .med-imgdesc {
    background-color: var(--catchup-gray-50);
    position: relative;
    padding: 1rem;
    border-radius: var(--rad-catchup-small);
    margin-top: 0.5rem;
  }
  .med-imgdesc-label {
    position: absolute;
    top: -0.75rem;
    background-color: var(--catchup-white);
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-small);
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    left: 0.5rem;
  }
`;
